/**
 * Import zaproszeń z pliku CSV (Etap 54, ADR-0060).
 *
 * Plik niesie to samo, co formularz „Zaproś pracownika” — bez numeru PESEL,
 * adresu i rachunku, które kandydat podaje sam w portalu onboardingu. Serwer
 * najpierw pokazuje podgląd z błędami przy wierszu, a zaproszenia wysyła
 * dopiero wtedy, gdy cały plik jest poprawny („wszystko albo nic”).
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, FormField, InfoBanner, Modal, Spinner } from '@k1p4/ui'
import {
  invitationImportTemplate,
  type InvitationImportFileError,
  type InvitationImportRow,
} from '@k1p4/shared'
import { ApiError, api, type InvitationImportPreview, type InvitationImportResult } from '../../api/client'
import styles from './InviteImportModal.module.css'

export interface InviteImportModalProps {
  open: boolean
  onClose: () => void
  onImported: () => void
}

/** Wzór pliku do pobrania — sam nagłówek, z BOM, żeby Excel czytał polskie znaki. */
function downloadTemplate(): void {
  const url = URL.createObjectURL(new Blob([invitationImportTemplate()], { type: 'text/csv;charset=utf-8' }))
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = 'zaproszenia.csv'
    link.click()
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function InviteImportModal({ open, onClose, onImported }: InviteImportModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const fileId = useId()
  const [csv, setCsv] = useState<string | null>(null)
  const [preview, setPreview] = useState<InvitationImportPreview | null>(null)
  const [result, setResult] = useState<InvitationImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const check = useMutation({
    mutationFn: (text: string) => api.previewInvitationImport(text),
    onSuccess: (data) => setPreview(data),
    onError: () => setError('inviteImport.errorGeneric'),
  })

  const send = useMutation({
    mutationFn: (text: string) => api.importInvitations(text),
    onSuccess: (data) => {
      setResult(data)
      void queryClient.invalidateQueries({ queryKey: ['invitations'] })
      onImported()
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiError && cause.code === 'IMPORT_CONFLICT') {
        setError('inviteImport.errorConflict')
        /* Ktoś zaprosił osobę z pliku między podglądem a zapisem — podgląd od nowa. */
        if (csv !== null) check.mutate(csv)
      } else if (cause instanceof ApiError && cause.code === 'EMAIL_NOT_VERIFIED') {
        setError('invite.errorEmailUnverified')
      } else {
        setError('inviteImport.errorGeneric')
      }
    },
  })

  function close(): void {
    setCsv(null)
    setPreview(null)
    setResult(null)
    setError(null)
    onClose()
  }

  function fileErrorText(fileError: InvitationImportFileError): string {
    switch (fileError.code) {
      case 'missingColumns':
        return t('inviteImport.fileErrors.missingColumns', {
          columns: fileError.columns.map((field) => t(`inviteImport.fields.${field}`)).join(', '),
        })
      case 'tooManyRows':
        return t('inviteImport.fileErrors.tooManyRows', { max: fileError.max })
      case 'unclosedQuote':
        return t('inviteImport.fileErrors.unclosedQuote', { line: fileError.line })
      default:
        return t(`inviteImport.fileErrors.${fileError.code}`)
    }
  }

  function rowErrors(row: InvitationImportRow): string {
    return row.errors
      .map((rowError) => `${t(`inviteImport.fields.${rowError.field}`)}: ${t(`inviteImport.errors.${rowError.code}`)}`)
      .join(' · ')
  }

  const rowsWithErrors = preview?.rows.filter((row) => row.errors.length > 0).length ?? 0
  const readyCount = preview?.rows.length ?? 0

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('inviteImport.title')}
      footer={
        result ? (
          <Button onClick={close}>{t('inviteImport.close')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={preview?.valid !== true || csv === null || send.isPending}
              onClick={() => {
                setError(null)
                if (csv !== null) send.mutate(csv)
              }}
            >
              {send.isPending ? (
                <Spinner label={t('inviteImport.sending')} />
              ) : (
                /* Liczba tylko przy poprawnym pliku — przy błędach nie obiecuje, ile wyjdzie. */
                preview?.valid === true
                  ? t('inviteImport.submit', { count: readyCount })
                  : t('inviteImport.submitIdle')
              )}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className={styles.result}>
          <InfoBanner tone="info">{t('inviteImport.done', { count: result.mailed })}</InfoBanner>
          {result.notMailed.length > 0 ? (
            <>
              <p className={styles.lead}>{t('inviteImport.notMailed')}</p>
              <ul className={styles.list}>
                {result.notMailed.map((item) => (
                  <li key={item.line} className={styles.row}>
                    <span className={styles.rowTitle}>{item.email}</span>
                    <span className={styles.link}>{item.joinUrl}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : (
        <>
          <p className={styles.lead}>{t('inviteImport.lead')}</p>
          <Button variant="secondary" size="sm" className={styles.template} onClick={downloadTemplate}>
            {t('inviteImport.template')}
          </Button>
          <FormField label={t('inviteImport.file')} htmlFor={fileId} hint={t('inviteImport.fileHint')}>
            <input
              id={fileId}
              className={styles.file}
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => {
                const file = event.target.files?.[0]
                setError(null)
                setPreview(null)
                if (!file) return
                void file.text().then((text) => {
                  setCsv(text)
                  check.mutate(text)
                })
              }}
            />
          </FormField>

          {error !== null ? (
            <InfoBanner tone="danger" className={styles.banner}>
              {t(error)}
            </InfoBanner>
          ) : null}
          {check.isPending ? <Spinner label={t('inviteImport.checking')} /> : null}

          {preview ? (
            <div className={styles.preview}>
              {preview.fileErrors.map((fileError) => (
                <InfoBanner key={fileError.code} tone="danger" className={styles.banner}>
                  {fileErrorText(fileError)}
                </InfoBanner>
              ))}
              {preview.fileErrors.length === 0 ? (
                <p className={styles.summary}>
                  {rowsWithErrors > 0
                    ? t('inviteImport.previewErrors', { count: rowsWithErrors })
                    : t('inviteImport.previewOk', { count: readyCount })}
                </p>
              ) : null}
              {rowsWithErrors > 0 ? (
                <InfoBanner tone="warning" className={styles.banner}>
                  {t('inviteImport.previewBlocked')}
                </InfoBanner>
              ) : null}
              {preview.ignoredColumns.length > 0 ? (
                <p className={styles.note}>
                  {t('inviteImport.ignored', { columns: preview.ignoredColumns.join(', ') })}
                </p>
              ) : null}
              {preview.rows.length > 0 ? (
                <ul className={styles.list}>
                  {preview.rows.map((row) => (
                    <li key={row.line} className={styles.row}>
                      <span className={styles.rowLine}>{t('inviteImport.line', { line: row.line })}</span>
                      <span className={styles.rowTitle}>
                        {[row.values.firstName, row.values.lastName].filter(Boolean).join(' ') || '—'}
                        {row.values.email ? ` · ${row.values.email}` : ''}
                      </span>
                      {row.errors.length > 0 ? (
                        <span className={styles.rowError}>{rowErrors(row)}</span>
                      ) : (
                        <span className={styles.rowOk}>{t('inviteImport.rowOk')}</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </Modal>
  )
}
